// src/services/api.js (v2 – FormData-safe + token refresh)

import axios from "axios";
import { API_PATHS, API_BASE_URL } from "../config";

const api = axios.create({
  baseURL: API_BASE_URL,
  // ✅ Do NOT force Content-Type globally.
  // Axios will set JSON automatically when sending plain objects,
  // and FormData needs multipart boundaries.
  headers: { Accept: "application/json" },
});

// Broadcast auth changes so App.js can react in the same tab
const notifyAuthChanged = () => {
  window.dispatchEvent(new Event("gg-auth-changed"));
};

// Request interceptor: attach Bearer token if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ✅ If we're sending FormData, ensure we DO NOT send application/json
    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      config.headers = config.headers || {};
      // Let axios/browser set multipart boundary correctly
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// === SILENT REFRESH TOKEN HANDLING ===
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Helper to centralize logout cleanup
const performLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("role");
  localStorage.removeItem("schoolName");
  notifyAuthChanged();
};

// Response interceptor with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    const url = originalRequest?.url || "";

    const is401 = error?.response?.status === 401;
    const isLoginRequest = url.includes(API_PATHS.LOGIN);
    const isRefreshRequest = API_PATHS.REFRESH
      ? url.includes(API_PATHS.REFRESH)
      : url.includes("/auth/refresh");

    if (is401 && !originalRequest._retry && !isLoginRequest && !isRefreshRequest) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            try {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            } catch (e) {
              reject(e);
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        performLogout();
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const refreshResp = await axios.post(
          `${API_BASE_URL}${API_PATHS.REFRESH || "/auth/refresh"}`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );

        const { access_token, role, school_name } = refreshResp.data || {};
        if (!access_token) throw new Error("No access token in refresh response");

        localStorage.setItem("token", access_token);
        localStorage.setItem("role", role || "");
        localStorage.setItem("schoolName", school_name || "");

        api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        notifyAuthChanged();
        onRefreshed(access_token);

        return api(originalRequest);
      } catch (refreshError) {
        performLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// === DEDICATED LOGIN FUNCTION (form-urlencoded required by FastAPI) ===
export const loginUser = async (email, password) => {
  const formData = new URLSearchParams();
  formData.append("username", (email || "").trim().toLowerCase());
  formData.append("password", password);

  const response = await axios.post(
    `${API_BASE_URL}${API_PATHS.LOGIN}`,
    formData,
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
  );

  const data = response.data || {};

  if (data.access_token) {
    localStorage.setItem("token", data.access_token);
    localStorage.setItem("role", data.role || "");
    localStorage.setItem("schoolName", data.school_name || "");
  }
  if (data.refresh_token) {
    localStorage.setItem("refreshToken", data.refresh_token);
  }

  notifyAuthChanged();
  return data;
};

export default api;